import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateCouponDto, QueryCouponsDto, UpdateCouponDto } from '@control-center/shared';

import {
  createCoupon,
  deleteCoupon,
  fetchCouponRedemptions,
  fetchCoupons,
  updateCoupon,
} from '../lib/coupons-api';

export const couponsKeys = {
  all: ['coupons'] as const,
  lists: () => [...couponsKeys.all, 'list'] as const,
  list: (filters: QueryCouponsDto) => [...couponsKeys.lists(), filters] as const,
  redemptions: (id: string) => [...couponsKeys.all, 'redemptions', id] as const,
};

export function useCoupons(filters: QueryCouponsDto = { limit: 50 }) {
  return useQuery({
    queryKey: couponsKeys.list(filters),
    queryFn: () => fetchCoupons(filters),
    staleTime: 30_000,
  });
}

export function useCreateCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCouponDto) => createCoupon(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: couponsKeys.all });
    },
  });
}

export function useUpdateCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCouponDto }) =>
      updateCoupon(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: couponsKeys.all });
    },
  });
}

export function useDeleteCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCoupon(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: couponsKeys.all });
    },
  });
}

/**
 * Quem usou o cupom, quem fechou e quanto custou.
 *
 * `enabled` por id: o painel só busca quando um cupom está aberto — não faz
 * sentido carregar resgate de cupom que ninguém clicou.
 *
 * `staleTime` curto (10s) porque durante uma campanha o número muda: o operador
 * atualiza a tela justamente pra ver se entrou mais alguém.
 */
export function useCouponRedemptions(couponId: string | null) {
  return useQuery({
    queryKey: couponsKeys.redemptions(couponId ?? ''),
    queryFn: () => fetchCouponRedemptions(couponId as string),
    enabled: Boolean(couponId),
    staleTime: 10_000,
  });
}
